import React, { useState, useEffect } from 'react';
import DatePicker from 'react-datepicker';
import { subDays, subMonths, startOfDay, endOfDay } from 'date-fns';
import 'react-datepicker/dist/react-datepicker.css';
import './CustomDateRangePicker.scss';

const CustomDateRangePicker = ({
  startDate,
  endDate,
  onChange,
  onClose,
  className = '',
  showPresets = true,
  customPresets = null
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedRange, setSelectedRange] = useState([startDate, endDate]);

  // Default presets
  const defaultPresets = [
    {
      label: 'Yesterday',
      getValue: () => {
        const yesterday = subDays(new Date(), 1);
        return [startOfDay(yesterday), endOfDay(yesterday)];
      }
    },
    {
      label: '3 months',
      getValue: () => {
        const threeMonthsAgo = subMonths(new Date(), 3);
        return [startOfDay(threeMonthsAgo), endOfDay(new Date())];
      }
    },
    {
      label: '6 months',
      getValue: () => {
        const sixMonthsAgo = subMonths(new Date(), 6);
        return [startOfDay(sixMonthsAgo), endOfDay(new Date())];
      }
    },
    {
      label: 'All time',
      getValue: () => {
        // Set a reasonable "all time" range (e.g., 2 years ago to now)
        const twoYearsAgo = subMonths(new Date(), 24);
        return [startOfDay(twoYearsAgo), endOfDay(new Date())];
      }
    }
  ];

  const presets = customPresets || defaultPresets;

  useEffect(() => {
    setSelectedRange([startDate, endDate]);
  }, [startDate, endDate]);

  const handleDateChange = (dates) => {
    const [start, end] = dates;
    setSelectedRange([start, end]);
    
    // Only call onChange when both dates are selected
    if (start && end) {
      onChange([start, end]);
    } else if (start && !end) {
      // First date selected, keep it in local state
      onChange([start, null]);
    }
  };

  const handlePresetClick = (preset) => {
    const [start, end] = preset.getValue();
    setSelectedRange([start, end]);
    onChange([start, end]);
  };

  const handleApply = () => {
    if (selectedRange[0] && selectedRange[1]) {
      onChange(selectedRange);
      setIsOpen(false);
      if (onClose) onClose();
    }
  };

  const handleCancel = () => {
    setSelectedRange([startDate, endDate]);
    setIsOpen(false);
    if (onClose) onClose();
  };

  return (
    <div className={`custom-date-range-picker ${className}`}>
      {/* Date Input Display */}
      <div 
        className="date-input-display"
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="date-range-text">
          {selectedRange[0] ? selectedRange[0].toLocaleDateString() : 'Start Date'}
          <span className="date-separator">→</span>
          {selectedRange[1] ? selectedRange[1].toLocaleDateString() : 'End Date'}
        </div>
        <div className="calendar-icon">
          <svg width="18" height="20" viewBox="0 0 18 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M16 2H15V0H13V2H5V0H3V2H2C0.89 2 0.01 2.9 0.01 4L0 18C0 19.1 0.89 20 2 20H16C17.1 20 18 19.1 18 18V4C18 2.9 17.1 2 16 2ZM16 18H2V7H16V18ZM4 9H9V14H4V9Z" fill="#737C8A"/>
          </svg>
        </div>
      </div>

      {/* Date Picker Dropdown */}
      {isOpen && (
        <div className="date-picker-dropdown">
          <div className="date-picker-container">
            <div className="calendar-section">
              <DatePicker
                selected={selectedRange[0]}
                onChange={handleDateChange}
                startDate={selectedRange[0]}
                endDate={selectedRange[1]}
                selectsRange
                inline
                monthsShown={1}
                calendarClassName="custom-calendar"
                dayClassName={(date) => {
                  const isInRange = selectedRange[0] && selectedRange[1] && 
                    date >= selectedRange[0] && date <= selectedRange[1];
                  const isStart = selectedRange[0] && 
                    date.toDateString() === selectedRange[0].toDateString();
                  const isEnd = selectedRange[1] && 
                    date.toDateString() === selectedRange[1].toDateString();
                  
                  if (isStart && isEnd) return 'range-start-end';
                  if (isStart) return 'range-start';
                  if (isEnd) return 'range-end';
                  if (isInRange) return 'range-middle';
                  return '';
                }}
              />
            </div>

            {/* Preset Buttons */}
            {showPresets && (
              <div className="presets-section">
                {presets.map((preset, index) => (
                  <button
                    key={index}
                    className="preset-button"
                    onClick={() => handlePresetClick(preset)}
                  >
                    {preset.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="action-buttons">
            <button 
              className="cancel-button"
              onClick={handleCancel}
            >
              Cancel
            </button>
            <button 
              className="apply-button"
              onClick={handleApply}
              disabled={!selectedRange[0] || !selectedRange[1]}
            >
              Apply
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomDateRangePicker;